/**
 * toc_spy_controller.js — highlights the table-of-contents entry for the
 * section currently being read.
 *
 * Enhancement only. The rail is server-rendered anchors and works with this
 * controller absent, disabled, or unsupported: all it adds is which entry
 * looks current.
 *
 * One IntersectionObserver over the headings, and no scroll listener. A scroll
 * handler here would run on every frame of a flick and would have to measure
 * element positions to decide anything, and measuring in a scroll handler is
 * how you get forced synchronous layout on the one interaction that has to
 * stay smooth.
 *
 * The rootMargin shrinks the viewport to a strip across its top fifth, so
 * "current" means the heading you have most recently scrolled up to rather
 * than whatever happens to be on screen.
 *
 * The strip starts at the very top rather than being inset. An inset strip
 * reads better while scrolling, but a heading reached by clicking its own
 * contents link lands flush against the top of the viewport, which would put
 * it above an inset strip and leave the entry the reader just clicked
 * unhighlighted. The one interaction the rail exists for has to work.
 *
 * Most of the time nothing is in the strip, because most of the time you are
 * mid-section. That is why _markCurrent holds the last match instead of
 * clearing: the highlight should persist through a section, not flicker off
 * the moment its heading scrolls away.
 */

import { Controller } from "@hotwired/stimulus";

const OBSERVER_OPTIONS = {
    rootMargin: "0px 0px -80% 0px",
    threshold: 0,
};

export default class extends Controller {
    static targets = ["link"];

    connect() {
        if (!("IntersectionObserver" in window)) return;

        this._linkByHeading = new Map();
        const headings = [];

        this.linkTargets.forEach((link) => {
            const id = decodeURIComponent((link.getAttribute("href") || "").slice(1));
            if (!id) return;
            const heading = document.getElementById(id);
            if (!heading) return;
            this._linkByHeading.set(heading, link);
            headings.push(heading);
        });

        if (!headings.length) return;

        // Document order, so "topmost intersecting" is a first() rather than a sort.
        this._headings = headings;
        this._visible = new Set();

        this._observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) this._visible.add(entry.target);
                else this._visible.delete(entry.target);
            });
            this._markCurrent();
        }, OBSERVER_OPTIONS);

        headings.forEach((heading) => this._observer.observe(heading));
    }

    disconnect() {
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
        this._linkByHeading = null;
        this._headings = null;
        this._visible = null;
    }

    /**
     * Light the entry for the first heading in document order that is inside
     * the band. When nothing is in the band -- mid-section, which is most of
     * the time -- the previous highlight stays put rather than flickering off.
     */
    _markCurrent() {
        const current = this._headings.find((heading) => this._visible.has(heading));
        if (!current) return;

        const link = this._linkByHeading.get(current);
        if (!link || link === this._current) return;

        if (this._current) {
            this._current.classList.remove("is-active");
            this._current.removeAttribute("aria-current");
        }
        link.classList.add("is-active");
        link.setAttribute("aria-current", "location");
        this._current = link;
    }
}
